import { useState } from 'react'
import { Button, Col, Result, Row, Space, Typography } from 'antd'
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Show } from '@refinedev/antd'
import { useOne } from '@refinedev/core'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { ReturnRoot } from '@repo/sdk'

import { DetailNotFound } from '../../components/DetailNotFound'
import { PageError } from '../../components/PageError'
import { StatusBadge } from '../../components/StatusBadge'
import { NicknameText } from '../../components/NicknameText'
import { useBackToList } from '../../shared/hooks/useBackToList'
import { formatReturnRefundAmount } from './formatReturnRefundAmount'
import { useReturnStatusConfig } from './returnStatusConfig'
import { useApproveReturn } from './useApproveReturn'
import { ReturnInfoCard } from './ReturnInfoCard'
import { ReturnOrderInfoCard } from './ReturnOrderInfoCard'
import { ReturnBuyerInfoCard } from './ReturnBuyerInfoCard'
import { ReturnTimelineCard } from './ReturnTimelineCard'
import { ReturnImagesCard } from './ReturnImagesCard'
import { RejectReturnModal } from './RejectReturnModal'
import { confirmPopup } from '@/components/popup'

export function ReturnDetail() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const orderId = searchParams.get('orderId')
  const navigate = useNavigate()
  const backToList = useBackToList('/returns')
  const { t } = useTranslation()
  const statusConfig = useReturnStatusConfig()
  const { approveReturn, isApproving } = useApproveReturn()
  const [rejectModalOpen, setRejectModalOpen] = useState(false)

  const { query } = useOne<ReturnRoot>({
    resource: 'return-list',
    id: id ?? '',
    meta: { orderId },
    queryOptions: { enabled: !!id && !!orderId },
  })

  const { data, isLoading, isError } = query
  const returnData = data?.data

  // The return endpoint needs both ids. Without `?orderId` the query stays disabled, which
  // looks exactly like "no data" — say what is actually missing instead of a bare 404.
  if (!orderId)
    return (
      <Result
        data-testid="return-detail-missing-order-id"
        status="warning"
        title={t('pages.returns.missingOrderId')}
        extra={
          <Button type="primary" onClick={() => void navigate('/returns')}>
            {t('common.buttons.backToList')}
          </Button>
        }
      />
    )
  if (!isLoading && isError) return <PageError onRetry={() => void query.refetch()} />
  if (!isLoading && !isError && !returnData) return <DetailNotFound backTo="/returns" />

  const isRequested = returnData?.status === 'REQUESTED'

  const handleApprove = () => {
    if (!id || !returnData) return
    // Server-decided amount (서버 주도 UI 정책), labelled as an estimate before the refund is issued.
    const refundAmount = formatReturnRefundAmount(t, returnData) ?? '-'
    confirmPopup({
      title: t('common.modals.approveReturnTitle'),
      content: (
        <>
          <p>{t('common.modals.approveReturnMessage', { amount: refundAmount })}</p>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            {t('common.modals.approveReturnWarning')}
          </p>
        </>
      ),
      okText: t('common.buttons.confirm'),
      cancelText: t('common.buttons.cancel'),
      okButtonProps: { 'data-testid': 'return-detail-approve-confirm-btn' },
      cancelButtonProps: { 'data-testid': 'return-detail-approve-cancel-btn' },
      onOk: () => approveReturn(id),
    })
  }

  return (
    <Show
      isLoading={isLoading}
      title={t('pages.returns.detailTitle')}
      headerButtons={
        <Space>
          {isRequested && (
            <>
              <Button
                data-testid="return-detail-approve-btn"
                type="primary"
                icon={<CheckOutlined />}
                loading={isApproving}
                onClick={handleApprove}
              >
                {t('common.buttons.approve')}
              </Button>
              <Button
                data-testid="return-detail-reject-btn"
                danger
                icon={<CloseOutlined />}
                // 승인 요청이 처리되는 동안에는 거절을 막는다 — 두 요청이 겹치면 뒤 요청이 실패한다(개발항목 294)
                disabled={isApproving}
                onClick={() => setRejectModalOpen(true)}
              >
                {t('common.buttons.reject')}
              </Button>
            </>
          )}
        </Space>
      }
    >
      <Button
        data-testid="return-detail-back-btn"
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={backToList}
        style={{ padding: 0, marginBottom: 16 }}
      >
        {t('common.buttons.backToList')}
      </Button>

      {returnData && (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {returnData.returnId.slice(0, 8)}...
            </Typography.Title>
            <Typography.Text>
              <NicknameText nickname={returnData.buyer.nickname} />
            </Typography.Text>
            <StatusBadge status={returnData.status} configMap={statusConfig} />
          </Space>

          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <ReturnInfoCard returnData={returnData} />
            </Col>
            <Col xs={24} lg={12}>
              <ReturnOrderInfoCard returnData={returnData} />
            </Col>
            <Col xs={24} lg={12}>
              <ReturnBuyerInfoCard returnData={returnData} />
            </Col>
            <Col xs={24} lg={12}>
              <ReturnTimelineCard returnData={returnData} />
            </Col>
            <Col xs={24}>
              <ReturnImagesCard returnData={returnData} />
            </Col>
          </Row>

          <RejectReturnModal
            returnId={returnData.returnId}
            open={rejectModalOpen}
            onClose={() => setRejectModalOpen(false)}
          />
        </>
      )}
    </Show>
  )
}
